import { useCallback } from 'react';
import { CustomerMfeLoader } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type {
  ICustomerMfeLoaderSettings,
  InitiateCallPayload,
  NavigateTarget,
} from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import { ContactType } from '@api/generated/graphql';
import type { Contact } from '@api/generated/graphql';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import AuthModule from '@modules/AuthModule';
import { resolveTenantId } from '@modules/AuthModule/lib/resolveOrgNodeId';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import environment from '@lib/environment';
import { resolveNavigateUrl } from '@lib/utils/resolveNavigateUrl';

type CustomerChangedPayload = {
  customerId: string;
  customerName: string;
  orgNodeId: string | null;
} | null;

export type ResolvedCustomer =
  | { id: string; name: string; orgNodeId?: string }
  | undefined;

interface CustomerMfeAdapterProps {
  /** Omit for all-tenants search (Mode 1). Pass the task's tenant-level OrgNode Guid to scope search to one tenant (Modes 2/3). */
  orgNodeId?: string;
  /** Seeds the resolved-customer view directly (Mode 3). Ignored unless orgNodeId is also set. Takes priority over phoneNumber. */
  customerId?: string;
  /** Seeds a phone-scoped search (Mode 2) — e.g. the task's caller ID. Ignored when customerId is also set. */
  phoneNumber?: string;
  /**
   * Marketing-campaign attribution for the task's call, for Book Job campaign
   * auto-selection.
   */
  campaignId?: string;
  /**
   * Call SID of the task's active call (`task.attributes.sessionSid`), so a job booked
   * from the MFE links back to the call. The monolith resolves the SID server-side and
   * books unlinked when it can't — absent is a supported outcome, not an error.
   */
  callSid?: string;
  /** Fired on resolve/clear. What "a resolved customer" means (task attribute, local state, ...) is the caller's call. */
  onCustomerChanged?: (customer: ResolvedCustomer) => void;
}

/**
 * Mounts customer-mfe. Callers bridge the resolve/clear signal onto whatever
 * "current customer" concept they own (TasksModule for a task, local state
 * for the home view) via onCustomerChanged.
 */
const CustomerMfeAdapter = ({
  orgNodeId,
  customerId,
  phoneNumber,
  campaignId,
  callSid,
  onCustomerChanged,
}: CustomerMfeAdapterProps) => {
  const { user } = useAuth();
  const { phoneConnected } = usePhoneState();

  const handleCustomerChanged = useCallback(
    (payload: CustomerChangedPayload) => {
      onCustomerChanged?.(
        payload
          ? {
              id: payload.customerId,
              name: payload.customerName,
              orgNodeId: payload.orgNodeId ?? undefined,
            }
          : undefined,
      );
    },
    [onCustomerChanged],
  );

  const handleInitiateCall = useCallback(
    (payload: InitiateCallPayload) => {
      if (
        SoftPhoneModule.isInCall() ||
        !phoneConnected ||
        !user?.outboundCallsAllowed
      ) {
        return;
      }
      // The outbound-call mutation this feeds (CallConfirmation -> useOutboundCall)
      // takes a numeric tenant id, not an OrgNode Guid — that surface is explicitly
      // out of scope for the orgNodeId migration, so bridge back via the user's
      // tenant list rather than threading the Guid further.
      const tenantId = resolveTenantId(user.org.tenants, payload.orgNodeId);
      if (!tenantId) {
        return;
      }
      // The intent payload only carries a raw phone string, not a full Contact.
      // Downstream reads are limited to `type`, `value`, and the optional
      // `memo` (AgentCallerIdConfirmationModal renders it via `?? undefined`,
      // so an absent memo here is a no-op, not a bug) — the required GraphQL
      // fields (`id`, notification flags) are never read, so stubbing them
      // would be fabricated data with no source of truth, not a safer
      // alternative.
      const contact = {
        type: ContactType.Phone,
        value: payload.contact,
      } as Contact;
      ModalsModule.openModal(ModalId.CallConfirmation, {
        contact,
        onClose: ModalsModule.closeActiveModal,
        customer: {
          customerName: payload.customerName,
          tenantId,
          customerId: String(payload.customerId),
        },
      });
    },
    [phoneConnected, user?.outboundCallsAllowed, user?.org.tenants],
  );

  const handleNavigate = useCallback(
    (target: NavigateTarget) => {
      const url = resolveNavigateUrl(target, user?.org.tenants ?? []);
      if (!url) {
        return;
      }
      AuthModule.resetAuthAge();
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [user?.org.tenants],
  );

  if (!user) {
    return null;
  }

  const hasResolvedCustomer =
    orgNodeId !== undefined && customerId !== undefined;

  const settings: ICustomerMfeLoaderSettings = {
    mfeUrl: environment.CUSTOMER_MFE_URL,
    agentInboxApiPath: `${environment.API_URL}/bffproxy/agent-inbox`,
    host: 'contactcenter',
    userId: user.id,
    accountId: user.accountId,
    contactCenterId: user.contactCenterId,
    networkOrgId: user.org.id,
    ...(orgNodeId !== undefined ? { orgNodeId } : {}),
    ...(hasResolvedCustomer ? { customerId } : {}),
    // Mode 2 requires an orgNodeId; Mode 3 (resolved customer) takes priority over it.
    ...(orgNodeId !== undefined &&
    !hasResolvedCustomer &&
    phoneNumber !== undefined
      ? { initialQuery: phoneNumber, initialTab: 'suggested' as const }
      : {}),
    ...(campaignId !== undefined ? { campaignId } : {}),
    ...(callSid !== undefined ? { callSid } : {}),
  };

  return (
    // w-100/h-100: the loader's web-component host has no intrinsic size, and
    // customer-mfe's own shell is height:100% all the way down (app.module.css) —
    // without an explicit height here that chain resolves against auto and collapses.
    <div className='w-100 h-100'>
      <CustomerMfeLoader
        settings={settings}
        onCustomerChanged={handleCustomerChanged}
        onInitiateCall={handleInitiateCall}
        onNavigate={handleNavigate}
      />
    </div>
  );
};

export default CustomerMfeAdapter;
